import { api } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import {
  type SiteDonationConfig,
  sanitizeSiteDonationConfig,
  siteDonationDefaults,
} from "@/lib/site-schema";
import { Check, Heart, Loader2, Save, X } from "lucide-react";
import { useEffect, useState } from "react";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; config: SiteDonationConfig }
  | { status: "error"; message: string };

type SaveState = "idle" | "saving" | "saved" | "error";

// Site-wide admin settings. Currently scoped to the site:donation doc;
// future site-level controls (theming, defaults, etc.) get sections in
// this same page rather than new top-level admin routes.
export function AdminSettings() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [save, setSave] = useState<SaveState>("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.site.donation
      .get()
      .then((config) => {
        if (cancelled) return;
        setState({ status: "ready", config: config ?? siteDonationDefaults() });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Failed to load settings.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function update(next: SiteDonationConfig) {
    setState({ status: "ready", config: next });
    setSave("idle");
  }

  async function saveNow() {
    if (state.status !== "ready") return;
    const parsed = sanitizeSiteDonationConfig(state.config);
    if (!parsed.ok) {
      setSave("error");
      setError(parsed.reason);
      return;
    }
    setSave("saving");
    setError("");
    try {
      const saved = await api.site.donation.save(parsed.value);
      setState({ status: "ready", config: saved });
      setSave("saved");
      setTimeout(() => setSave("idle"), 1800);
    } catch (err) {
      setSave("error");
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  if (state.status === "loading") {
    return (
      <div className="grid h-96 place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <p className="text-sm text-red-600 dark:text-red-400">{state.message}</p>
      </div>
    );
  }

  const config = state.config;

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-8">
        <h1 className="display-title text-[clamp(2rem,4vw,3rem)] leading-tight">Site settings</h1>
        <p className="mt-1 text-sm text-muted">
          Configuration that applies to the platform itself, independent of any one event.
        </p>
      </header>

      <section className="surface-card p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-accent inline-flex items-center gap-2">
              <Heart className="h-3.5 w-3.5" strokeWidth={1.75} />
              General donation fund
            </p>
            <h2 className="mt-2 font-display text-2xl font-light text-ink">The /donate page</h2>
            <p className="mt-1 text-sm text-muted">
              Visible whenever a visitor lands at <code>/donate</code>. Independent of any event.
            </p>
          </div>
          <SaveButton state={save} error={error} onClick={() => void saveNow()} />
        </div>

        <div className="mt-8 grid gap-6">
          <ToggleRow
            label="Show the /donate page"
            description="When off, /donate shows a coming-soon state instead of the donation card."
            checked={config.enabled}
            onChange={(v) => update({ ...config, enabled: v })}
          />
          <ToggleRow
            label="Stripe checkout ready"
            description="Leave off until live Stripe keys have been provisioned and a $1 dry-run charge has succeeded. The donate button stays disabled while this is off."
            checked={config.stripeReady}
            onChange={(v) => update({ ...config, stripeReady: v })}
          />

          <TextField
            label="Title"
            value={config.title}
            onChange={(v) => update({ ...config, title: v })}
            hint="Headline of the donation card."
          />
          <TextArea
            label="Description"
            value={config.description}
            onChange={(v) => update({ ...config, description: v })}
            hint="Short paragraph under the headline. Up to 600 characters."
          />

          <AmountsField
            amounts={config.suggestedAmounts}
            onChange={(next) => update({ ...config, suggestedAmounts: next })}
          />

          <ToggleRow
            label="Allow custom amount"
            description="Lets visitors type their own amount. Recommended."
            checked={config.allowCustom}
            onChange={(v) => update({ ...config, allowCustom: v })}
          />

          <GoalField
            goalCents={config.goalCents}
            onChange={(next) => update({ ...config, goalCents: next })}
          />
        </div>
      </section>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="font-medium text-ink">{label}</p>
        <p className="mt-1 text-sm text-muted">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        aria-label={label}
        className={cn(
          "relative h-9 w-16 shrink-0 rounded-full border transition-colors focus-ring",
          checked ? "border-accent bg-accent" : "border-line/70 bg-surface",
        )}
      >
        <span
          className={cn(
            "absolute top-1 left-1 grid h-7 w-7 place-items-center rounded-full bg-surface-raised transition-transform shadow",
            checked ? "translate-x-7 text-accent-ink" : "translate-x-0 text-muted",
          )}
        >
          {checked ? (
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
          ) : (
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          )}
        </span>
      </button>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 block w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      />
      {hint ? <span className="mt-1 block text-xs text-subtle">{hint}</span> : null}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="mt-1.5 block w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      />
      {hint ? <span className="mt-1 block text-xs text-subtle">{hint}</span> : null}
    </label>
  );
}

function AmountsField({
  amounts,
  onChange,
}: {
  amounts: number[];
  onChange: (next: number[]) => void;
}) {
  function setAt(idx: number, value: string) {
    // Allow 0 transiently so the admin can clear a field and type a new
    // value without it snapping to 1 on every keystroke. The blur
    // handler below clamps to a minimum of 1 before the save round-trip
    // (the schema sanitizer would otherwise reject 0 with a 400).
    const n = Math.max(0, Math.round(Number(value) || 0));
    const next = amounts.slice();
    next[idx] = n;
    onChange(next);
  }
  function clampAt(idx: number) {
    if (amounts[idx] >= 1) return;
    const next = amounts.slice();
    next[idx] = 1;
    onChange(next);
  }
  function removeAt(idx: number) {
    if (amounts.length <= 1) return;
    onChange(amounts.filter((_, i) => i !== idx));
  }
  function addOne() {
    if (amounts.length >= 6) return;
    onChange([...amounts, amounts[amounts.length - 1] * 2 || 100]);
  }
  return (
    <div>
      <p className="text-sm font-medium text-ink">Suggested amounts (USD)</p>
      <p className="mt-1 text-xs text-subtle">1 to 6 buttons. Whole dollars.</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {amounts.map((amt, idx) => (
          <div
            key={idx}
            className="inline-flex items-center gap-1 rounded-full border border-line/70 bg-surface px-3 py-1.5"
          >
            <span className="text-sm text-muted">$</span>
            <input
              type="number"
              min={1}
              value={amt}
              onChange={(e) => setAt(idx, e.target.value)}
              onBlur={() => clampAt(idx)}
              className="w-16 bg-transparent text-sm text-ink outline-none"
              aria-label={`Suggested amount ${idx + 1} in whole dollars`}
            />
            <button
              type="button"
              onClick={() => removeAt(idx)}
              disabled={amounts.length <= 1}
              aria-label={`Remove $${amt}`}
              className="text-subtle hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addOne}
          disabled={amounts.length >= 6}
          className="inline-flex items-center rounded-full border border-line/70 px-3 py-1.5 text-sm text-muted hover:border-accent hover:text-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add
        </button>
      </div>
    </div>
  );
}

function GoalField({
  goalCents,
  onChange,
}: {
  goalCents: number | undefined;
  onChange: (next: number | undefined) => void;
}) {
  const dollars = goalCents ? String(Math.round(goalCents / 100)) : "";
  return (
    <div>
      <p className="text-sm font-medium text-ink">Fundraising target (optional)</p>
      <p className="mt-1 text-xs text-subtle">
        When set, the donation card shows a progress bar against this goal. Leave blank for
        open-ended giving.
      </p>
      <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-line/70 bg-surface px-3 py-1.5">
        <span className="text-sm text-muted">$</span>
        <input
          type="number"
          min={0}
          step={100}
          placeholder="0"
          value={dollars}
          onChange={(e) => {
            const v = e.target.value.trim();
            if (!v) {
              onChange(undefined);
              return;
            }
            const n = Math.max(0, Math.round(Number(v) || 0));
            onChange(n === 0 ? undefined : n * 100);
          }}
          className="w-24 bg-transparent text-sm text-ink outline-none"
          aria-label="Fundraising target in whole dollars"
        />
      </div>
    </div>
  );
}

function SaveButton({
  state,
  error,
  onClick,
}: {
  state: SaveState;
  error: string;
  onClick: () => void;
}) {
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-4 py-2 text-sm font-medium text-accent-ink dark:text-accent">
        <Check className="h-4 w-4" strokeWidth={1.75} />
        Saved
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === "saving"}
      title={error || undefined}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas hover:bg-ink/90 dark:bg-accent dark:text-accent-on",
        state === "saving" && "opacity-60 cursor-wait",
        state === "error" && "ring-2 ring-red-500/50",
      )}
    >
      {state === "saving" ? (
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
      ) : (
        <Save className="h-4 w-4" strokeWidth={1.75} />
      )}
      Save
    </button>
  );
}
